import { signData, getChainId } from './rpc';
import CONTRACT_ABI from '../../util/erc20-classic.json';
import { ethers } from 'ethers';

const MAX_INT = '0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff'; // MAX_UINT256

// Standard EIP712Domain with version (most ERC-2612 tokens)
const EIP712Domain = [
  { name: "name", type: "string" },
  { name: "version", type: "string" },
  { name: "chainId", type: "uint256" },
  { name: "verifyingContract", type: "address" },
];

// EIP712Domain without version (UNI token and some older tokens)
const EIP712DomainNoVersion = [
  { name: "name", type: "string" },
  { name: "chainId", type: "uint256" },
  { name: "verifyingContract", type: "address" },
];

const createTypedDaiData = (message, domain) => {
  const typedData = {
    types: {
      EIP712Domain,
      Permit: [
        { name: "holder", type: "address" },
        { name: "spender", type: "address" },
        { name: "nonce", type: "uint256" },
        { name: "expiry", type: "uint256" },
        { name: "allowed", type: "bool" },
      ],
    },
    primaryType: "Permit",
    domain,
    message,
  };

  return typedData;
};

const createTypedERC2612Data = (message, domain) => {
  // Use appropriate EIP712Domain structure based on whether domain has version
  const domainType = domain.version !== undefined && domain.version !== null
    ? EIP712Domain  // Standard domain with version
    : EIP712DomainNoVersion; // Domain without version (UNI token, etc.)
  
  const typedData = {
    types: {
      EIP712Domain: domainType,
      Permit: [
        { name: "owner", type: "address" },
        { name: "spender", type: "address" },
        { name: "value", type: "uint256" },
        { name: "nonce", type: "uint256" },
        { name: "deadline", type: "uint256" },
      ],
    },
    primaryType: "Permit",
    domain,
    message,
  };

  return typedData;
};

/**
 * Gets the exact token name from the contract
 * CRITICAL: Must use the exact name returned by the contract (e.g., "Uniswap" not "UNI")
 * This is used in the EIP-712 domain separator and must match exactly
 */
const getTokenName = async (provider, address) => {
  const pC = new ethers.Contract(address, CONTRACT_ABI, provider)
  return await pC.name()
}

/**
 * Checks if token contract uses version in EIP712Domain
 * Some tokens (like UNI) don't include version in their domain separator
 * 
 * UNI contract uses: keccak256("EIP712Domain(string name,uint256 chainId,address verifyingContract)")
 * Standard tokens use: keccak256("EIP712Domain(string name,string version,uint256 chainId,address verifyingContract)")
 */
const checkDomainUsesVersion = async (provider, tokenAddress) => {
  // Known tokens that don't use version in domain separator
  const TOKENS_WITHOUT_VERSION = [
    '0x1f9840a85d5aF5bf1D1762F925BDADdC4201F984', // UNI token (mainnet)
  ];
  
  // Check if this is a known token without version
  if (TOKENS_WITHOUT_VERSION.some(addr => addr.toLowerCase() === tokenAddress.toLowerCase())) {
    return false;
  }
  
  // Try to read DOMAIN_TYPEHASH constant from contract to verify
  try {
    const pC = new ethers.Contract(tokenAddress, CONTRACT_ABI, provider);
    await pC.DOMAIN_TYPEHASH(); // Just check if it exists
    // If it exists, we could potentially decode it, but for now use known list
    // Default to true (most tokens use version)
    return true;
  } catch {
    // If DOMAIN_TYPEHASH doesn't exist, assume standard (with version)
    return true;
  }
}

const getTokenVersion = async (provider, tokenAddress) => {
  try {
    const pC = new ethers.Contract(tokenAddress, CONTRACT_ABI, provider);
    
    // Try to get version from contract (some tokens have version() function)
    try {
      const version = await pC.version();
      // Convert to string if it's a number or BigInt
      if (typeof version === 'string') {
        return version;
      } else if (typeof version === 'bigint' || typeof version === 'number') {
        return version.toString();
      }
      return String(version);
    } catch {
      // Try VERSION() if version() doesn't exist (some tokens use uppercase)
      try {
        const version = await pC.VERSION();
        if (typeof version === 'string') {
          return version;
        } else if (typeof version === 'bigint' || typeof version === 'number') {
          return version.toString();
        }
        return String(version);
      } catch {
        // If no version function exists, default to "1" (standard for most ERC-2612 permits)
        return '1';
      }
    }
  } catch (error) {
    // If contract call fails entirely, default to "1"
    return '1';
  }
}

/**
 * Gets the EIP-712 domain separator for permit signatures
 * CRITICAL: All values must be exact:
 * - name: Must be the exact token name from contract.name() (e.g., "Uniswap" for UNI token)
 * - version: Must be from contract.version() or "1" if not available (unless token doesn't use version)
 * - chainId: Must be the actual chain ID from provider (1 for mainnet)
 * - verifyingContract: Must be the exact token contract address
 * 
 * @param {Object} provider - Ethers provider
 * @param {string|Object} token - Token address or token object with permitDomainFormat
 * @param {string} permitDomainFormat - Optional: "standard" or "no_version" from API response
 */
const getDomain = async (provider, token, permitDomainFormat = null) => {
  if (typeof token !== 'string') {
    return token;
  }

  const tokenAddress = token;

  // CRITICAL: Get exact token name from contract (e.g., "Uniswap" not "UNI")
  // Using wrong name causes "permit: unauthorized" error
  // DO NOT use symbol, display name, or hardcoded values
  const name = await getTokenName(provider, tokenAddress);

  // CRITICAL: Get actual chainId from provider (1 for mainnet, etc.)
  // Handle different provider types: ethers v6 has getNetwork(), others need RPC call
  // IMPORTANT: chainId must be a number, not a string
  let chainId;
  if (provider.getNetwork) {
    // ethers v6 provider
    const network = await provider.getNetwork();
    chainId = Number(network.chainId);
  } else {
    // Use RPC helper for other provider types
    const chainIdHex = await getChainId(provider);
    chainId = parseInt(chainIdHex, 16);
  }

  // CRITICAL: Determine if token uses version in domain separator
  // Priority: 1. Use permitDomainFormat from API (backend detection)
  //           2. Fall back to frontend detection if not provided
  let usesVersion;
  if (permitDomainFormat) {
    // Use format from backend API (most reliable)
    usesVersion = permitDomainFormat === 'standard';
    if (process.env.NODE_ENV === "development") {
      console.log('Using permitDomainFormat from API:', permitDomainFormat);
    }
  } else {
    // Fall back to frontend detection (for backward compatibility)
    usesVersion = await checkDomainUsesVersion(provider, tokenAddress);
    if (process.env.NODE_ENV === "development") {
      console.log('Using frontend detection for domain format');
    }
  }
  
  // CRITICAL: Get version from contract (defaults to "1" if not available)
  // Only include version if token uses it in domain separator
  let version = null;
  if (usesVersion) {
    version = await getTokenVersion(provider, tokenAddress);
  }

  // CRITICAL: verifyingContract must be the exact token address
  const domain = usesVersion
    ? {
        name, // Exact name from contract (e.g., "Uniswap")
        version, // From contract or "1"
        chainId, // Actual chain ID from provider (as number)
        verifyingContract: tokenAddress // Exact token contract address
      }
    : {
        name, // Exact name from contract (e.g., "Uniswap")
        chainId, // Actual chain ID from provider (as number)
        verifyingContract: tokenAddress // Exact token contract address
        // NO version field for tokens that don't use it
      };
  
  // DEBUG: Verify domain values are correct
  if (process.env.NODE_ENV === "development") {
    console.log('Domain Values:', {
      name: domain.name,
      nameType: typeof domain.name,
      version: domain.version || 'N/A (token does not use version)',
      versionType: typeof domain.version,
      usesVersion: usesVersion,
      permitDomainFormat: permitDomainFormat || 'detected',
      chainId: domain.chainId,
      chainIdType: typeof domain.chainId, // Should be "number"
      verifyingContract: domain.verifyingContract
    });
    
    // Warn if chainId is not a number
    if (typeof domain.chainId !== 'number') {
      console.warn('WARNING: chainId is not a number:', domain.chainId, typeof domain.chainId);
    }
  }
  
  return domain;
};

/**
 * Generates DAI-style permit signature (EIP-712)
 * CRITICAL: Must use exact values from contract:
 * - Domain: name, version, chainId, verifyingContract from contract
 * - Nonce: Must be current nonce from contract.nonces(holder)
 */
export const signDaiPermit = async (
  provider,
  token,
  holder,
  spender,
  expiry,
  nonce,
) => {
  const tokenAddress = (token).verifyingContract || token;

  const pC = new ethers.Contract(tokenAddress, CONTRACT_ABI, provider)
  
  // CRITICAL: Get current nonce from contract - must match exactly
  // Using wrong nonce causes "permit: unauthorized" error
  // IMPORTANT: Fetch fresh nonce from contract (do not cache)
  const n = await pC.nonces(holder);

  // CRITICAL: Use BigInt directly for nonce in message (not hex string)
  // EIP-712 expects uint256 as BigInt or number, not hex string
  const message = {
    holder,
    spender,
    nonce: n, // Use BigInt directly (ethers.js signTypedData handles BigInt correctly)
    expiry: expiry || MAX_INT,
    allowed: true,
  };

  // CRITICAL: Domain must use exact values from contract
  const domain = await getDomain(provider, token);
  
  // DEBUG: Log all values for debugging
  if (process.env.NODE_ENV === "development") {
    console.log('=== DAI Permit Signature Debug ===');
    console.log('Domain:', {
      name: domain.name,
      version: domain.version,
      chainId: domain.chainId,
      verifyingContract: domain.verifyingContract
    });
    console.log('DAI Permit Message:', {
      holder: message.holder,
      spender: message.spender,
      nonce: message.nonce.toString(),
      expiry: message.expiry.toString(),
      allowed: message.allowed
    });
    console.log('Nonce Verification:', {
      contractNonce: n.toString(),
      messageNonce: message.nonce.toString(),
      match: n.toString() === message.nonce.toString()
    });
  }
  
  const typedData = createTypedDaiData(message, domain);
  const sig = await signData(provider, holder, typedData);
  
  // CRITICAL: Ensure v is 27 or 28 (not 0 or 1)
  let v = sig.v;
  if (v === 0 || v === 1) {
    v = v + 27; // Convert recovery ID to v value
  }
  
  // DEBUG: Log signature components
  if (process.env.NODE_ENV === "development") {
    console.log('Signature Components:', {
      v: v,
      r: sig.r,
      s: sig.s
    });
    console.log('=== End DAI Permit Signature Debug ===');
  }

  return { 
    ...sig, 
    v, // Use corrected v value (27 or 28)
    ...message 
  };
};

/**
 * Generates ERC-2612 standard permit signature (EIP-712)
 * CRITICAL: Must use exact values from contract:
 * - Domain: name, version, chainId, verifyingContract from contract
 * - Nonce: Must be current nonce from contract.nonces(owner)
 * 
 * @param {Object} provider - Ethers provider
 * @param {string|Object} token - Token address or token object
 * @param {string} owner - Owner address
 * @param {string} spender - Spender address
 * @param {string} value - Permit value (default: MAX_INT)
 * @param {number|string} deadline - Permit deadline
 * @param {number|string} nonce - Permit nonce (optional, fetched from contract if not provided)
 * @param {string} permitDomainFormat - Optional: "standard" or "no_version" from API response
 * 
 * Common errors:
 * - "permit: unauthorized" - Usually caused by wrong token name, nonce, version, chainId, or verifyingContract
 * - Example: UNI token requires name: "Uniswap" (not "UNI"), nonce: 0 (must match contract), no version field, chainId: 1
 */
export const signERC2612Permit = async (
  provider,
  token,
  owner,
  spender,
  value = MAX_INT,
  deadline,
  nonce,
  permitDomainFormat = null,
) => {
  const tokenAddress = (token).verifyingContract || token;

  const pC = new ethers.Contract(tokenAddress, CONTRACT_ABI, provider)
  
  // CRITICAL: Get current nonce from contract - must match exactly
  // Using wrong nonce causes "permit: unauthorized" error
  // Example: If contract.nonces(owner) returns 0, signature must use nonce 0
  // IMPORTANT: Fetch fresh nonce from contract (do not cache)
  const n = await pC.nonces(owner);

  // CRITICAL: Use BigInt directly for nonce in message (not hex string)
  // EIP-712 expects uint256 as BigInt or number, not hex string
  // IMPORTANT: For UNI token (and similar tokens with uint96 amounts), the permit function
  // accepts uint(-1) (MAX_UINT256) as a special case and converts it to uint96(-1) internally.
  // The value in the EIP-712 message should be MAX_UINT256 (as uint256), not a 96-bit value.
  const message = {
    owner,
    spender,
    value, // MAX_UINT256 - contract will handle conversion to uint96(-1) if needed
    nonce: n, // Use BigInt directly (ethers.js signTypedData handles BigInt correctly)
    deadline: deadline || MAX_INT,
  };

  // CRITICAL: Domain must use exact values from contract:
  // - name: Exact token name from contract.name() (e.g., "Uniswap" for UNI, not "UNI")
  // - version: From contract.version() or "1" if not available (unless permitDomainFormat is "no_version")
  // - chainId: Actual chain ID from provider (1 for mainnet)
  // - verifyingContract: Exact token contract address
  // Use permitDomainFormat from API if provided (backend detection is more reliable)
  const domain = await getDomain(provider, token, permitDomainFormat);
  
  // DEBUG: Log all values for debugging "permit: unauthorized" errors
  if (process.env.NODE_ENV === "development") {
    console.log('=== ERC-2612 Permit Signature Debug ===');
    console.log('Domain:', {
      name: domain.name,              // Should be exact from contract.name() (e.g., "Uniswap")
      version: domain.version,        // Should be from contract.version() or "1"
      chainId: domain.chainId,        // Should be number (1 for mainnet)
      verifyingContract: domain.verifyingContract  // Should be token address
    });
    console.log('Permit Message:', {
      owner: message.owner,           // User's wallet address
      spender: message.spender,       // TokenHandlerComplete contract address
      value: message.value,           // MAX_UINT256 for max allowance
      nonce: message.nonce.toString(), // Should match contract.nonces(owner)
      deadline: message.deadline.toString() // Unix timestamp
    });
    console.log('Nonce Verification:', {
      contractNonce: n.toString(),    // From contract.nonces(owner)
      messageNonce: message.nonce.toString(), // In permit message
      match: n.toString() === message.nonce.toString() // Should be true
    });
  }
  
  const typedData = createTypedERC2612Data(message, domain);
  
  // DEBUG: Log full typed data structure
  if (process.env.NODE_ENV === "development") {
    console.log('Full Typed Data:', JSON.stringify({
      types: typedData.types,
      primaryType: typedData.primaryType,
      domain: typedData.domain,
      message: {
        ...typedData.message,
        nonce: typedData.message.nonce.toString(), // Convert BigInt for JSON
        value: typedData.message.value.toString(),
        deadline: typedData.message.deadline.toString()
      }
    }, null, 2));
  }
  
  const sig = await signData(provider, owner, typedData);
  
  // CRITICAL: Ensure v is 27 or 28 (not 0 or 1)
  // ethers.js Signature.from() returns v as 0 or 1 (recovery ID)
  // EIP-712 signatures need v as 27 or 28
  let v = sig.v;
  if (v === 0 || v === 1) {
    v = v + 27; // Convert recovery ID to v value
  }
  
  // DEBUG: Log signature components
  if (process.env.NODE_ENV === "development") {
    console.log('Signature Components:', {
      v: v,                           // Should be 27 or 28
      r: sig.r,                       // Should be 32 bytes hex (66 chars including "0x")
      s: sig.s,                       // Should be 32 bytes hex (66 chars including "0x")
      rLength: sig.r.length,          // Should be 66
      sLength: sig.s.length           // Should be 66
    });
    console.log('=== End ERC-2612 Permit Signature Debug ===');
  }

  return { 
    ...sig, 
    v, // Use corrected v value (27 or 28)
    ...message 
  };
};
